/**
 * Coloration syntaxique du code affiché par le portail.
 *
 * Le moteur est `highlight.js`, **chargé à la demande** : il ne doit pas peser
 * sur le paquet d'entrée, qu'un utilisateur télécharge pour arriver sur une liste
 * d'applications. Seuls quatre langages sont enregistrés — c'est tout ce qu'un
 * artifact contient.
 *
 * Deux exigences gouvernent ce module, et expliquent sa forme.
 *
 * **L'alignement doit être exact.** Le comparatif rend le code ligne à ligne, et
 * l'éditeur superpose la coloration à une zone de saisie transparente : le
 * moindre caractère ajouté, retiré ou déplacé décale le texte coloré du texte
 * réel, et le curseur cesse de tomber là où il devrait. Le balisage produit
 * n'entoure donc jamais que du texte échappé, sans en modifier un caractère.
 *
 * **Le balisage est injecté tel quel** (`dangerouslySetInnerHTML`), ce qui n'est
 * acceptable que parce que `highlight.js` échappe son entrée : le code d'un
 * utilisateur ne peut pas en ressortir sous forme de balises. Ce module ne
 * ré-inverse aucun échappement — `splitHighlightedLines` ne fait que déplacer des
 * balises `<span>` déjà produites.
 */

/** Langages enregistrés. Le reste est traité comme du texte. */
export type HighlightLanguage = 'xml' | 'javascript' | 'typescript' | 'css';

/**
 * Taille au-delà de laquelle la coloration est abandonnée.
 *
 * Un artifact peut peser jusqu'à 1 Mo. Coloriser un tel volume à chaque frappe
 * figerait l'onglet ; mieux vaut un code lisible en noir et blanc qu'un champ de
 * saisie qui ne répond plus. L'interface **dit** que la coloration est désactivée
 * plutôt que de laisser croire à une panne.
 */
export const MAX_HIGHLIGHT_CHARACTERS = 200_000;

/** Résultat d'une colorisation. */
export interface HighlightResult {
  /** Document entier, sauts de ligne compris : ce que consomme l'éditeur. */
  html: string;
  /** Une entrée par ligne, autonome : ce que consomme le comparatif. */
  lines: string[];
}

/**
 * Devine le langage d'un code.
 *
 * `highlightAuto` de la bibliothèque n'est pas utilisé : il tranche sur des
 * heuristiques statistiques et se trompe régulièrement sur un fragment, alors que
 * nous savons précisément ce que nous manipulons — un artifact est une page HTML
 * autonome ou un module React. Le langage `xml` a de plus une propriété
 * décisive : il colore lui-même le contenu des balises `<style>` et `<script>`,
 * ce qui couvre l'essentiel d'un artifact statique.
 */
export function detectLanguage(code: string): HighlightLanguage {
  const debut = code.slice(0, 4000);

  if (/^\s*(<!doctype|<html|<head|<body|<meta|<style|<div|<main|<section)/i.test(debut)) {
    return 'xml';
  }
  // Un module qui contient du balisage est du JSX, pas du HTML.
  if (/^\s*(import|export)\s/m.test(debut)) {
    return /\.tsx?|:\s*(string|number|boolean)\b|interface\s+\w+/.test(debut)
      ? 'typescript'
      : 'javascript';
  }
  if (/<[a-z][\w-]*[\s/>]/i.test(debut)) return 'xml';
  if (/^\s*[.#@:a-z][\w-]*\s*\{/im.test(debut) && !/[;{]\s*(function|const|let)\b/.test(debut)) {
    return 'css';
  }
  return 'javascript';
}

/** Un jeton du balisage produit par le moteur. */
type Jeton =
  { type: 'ouvrant'; brut: string } | { type: 'fermant' } | { type: 'texte'; brut: string };

/** Découpe le balisage en balises `<span>` ouvrantes, fermantes et texte. */
function tokeniser(html: string): Jeton[] {
  const jetons: Jeton[] = [];
  const motif = /<span\b[^>]*>|<\/span>|[^<]+/g;
  for (const [brut] of html.matchAll(motif)) {
    if (brut.startsWith('</span')) jetons.push({ type: 'fermant' });
    else if (brut.startsWith('<span')) jetons.push({ type: 'ouvrant', brut });
    else jetons.push({ type: 'texte', brut });
  }
  return jetons;
}

/**
 * Découpe un balisage colorisé en une entrée autonome par ligne.
 *
 * C'est la seule partie réellement délicate. Une construction s'étend souvent sur
 * plusieurs lignes — un bloc CSS, un commentaire, une chaîne de gabarit — et le
 * moteur l'entoure alors d'un `<span>` unique traversant les sauts de ligne.
 * Couper naïvement sur `\n` produirait des lignes au balisage déséquilibré :
 * certaines ouvriraient un `<span>` jamais refermé, d'autres refermeraient un
 * `<span>` jamais ouvert. Le navigateur « répare » ce genre de fragment à sa
 * façon, et la coloration part alors en cascade sur tout le reste du document.
 *
 * Chaque ligne referme donc les `<span>` encore ouverts à son terme, et la
 * suivante les réouvre à l'identique. Le **texte** n'est jamais touché : seul le
 * balisage est déplacé.
 */
export function splitHighlightedLines(html: string): string[] {
  const lignes: string[] = [];
  const pile: string[] = [];
  let courante = '';

  /** Termine la ligne en refermant ce qui reste ouvert, puis en rouvre autant. */
  const retourLigne = (): void => {
    lignes.push(courante + '</span>'.repeat(pile.length));
    courante = pile.join('');
  };

  for (const jeton of tokeniser(html)) {
    if (jeton.type === 'ouvrant') {
      pile.push(jeton.brut);
      courante += jeton.brut;
      continue;
    }
    if (jeton.type === 'fermant') {
      pile.pop();
      courante += '</span>';
      continue;
    }

    // Un texte échappé ne contient ni `<` ni saut de ligne à l'intérieur d'une
    // entité : la découpe est donc sûre.
    const segments = jeton.brut.split('\n');
    courante += segments[0] ?? '';
    for (const segment of segments.slice(1)) {
      retourLigne();
      courante += segment;
    }
  }

  lignes.push(courante + '</span>'.repeat(pile.length));
  return lignes;
}

/** Moteur chargé, prêt à colorier. */
export interface Highlighter {
  highlight(code: string, language?: HighlightLanguage): HighlightResult;
}

/**
 * Chargement du moteur, mutualisé entre tous les appelants.
 *
 * Plusieurs composants peuvent le demander en même temps — l'éditeur et le
 * comparatif cohabitent sur le formulaire de mise à jour : la promesse est
 * mémorisée pour qu'un seul téléchargement ait lieu.
 */
let chargement: Promise<Highlighter> | undefined;

/** Charge le moteur et enregistre les langages utiles. */
export function loadHighlighter(): Promise<Highlighter> {
  chargement ??= (async () => {
    const [{ default: hljs }, xml, javascript, typescript, css] = await Promise.all([
      import('highlight.js/lib/core'),
      import('highlight.js/lib/languages/xml'),
      import('highlight.js/lib/languages/javascript'),
      import('highlight.js/lib/languages/typescript'),
      import('highlight.js/lib/languages/css'),
    ]);

    hljs.registerLanguage('xml', xml.default);
    hljs.registerLanguage('javascript', javascript.default);
    hljs.registerLanguage('typescript', typescript.default);
    hljs.registerLanguage('css', css.default);

    return {
      highlight(code, language) {
        const langue = language ?? detectLanguage(code);
        // `ignoreIllegals` : un artifact en cours de saisie est souvent
        // syntaxiquement incomplet. Sans cette option, le moteur abandonne à la
        // première construction interdite et la coloration disparaît d'un coup
        // au milieu d'une frappe.
        const { value } = hljs.highlight(code, { language: langue, ignoreIllegals: true });
        return { html: value, lines: splitHighlightedLines(value) };
      },
    } satisfies Highlighter;
  })();

  return chargement;
}
